export const UserJourneys = {
  Onboarding: {
    Type: "onboarding" as const,
    Name: "Initial Setup",
    Steps: [
      "signup",
      "business_goal",
      "first_product",
      "first_catalog",
      "catalog_product",
      "catalog_published",
    ] as const,
  },
  ProductSetup: {
    Type: "product_setup" as const,
    Name: "Product Management",
    Steps: [
      "add_product",
      "import_preparation",
      "import_products",
      "shopify-import",
      "csv-import",
      "upload_products",
      "category_setup",
      "create_collections",
      "add_fields",
      "add_variants",
      "bulk_update",
    ] as const,
  },
  CatalogCreation: {
    Type: "catalog_creation" as const,
    Name: "Catalog Creation",
    Steps: [
      "start_catalog",
      "pdf-import",
      "first_page",
      //create
      "discover_builder",
      "use_auto_grid",
      "use_product_list",
      "use_layout",
      "save_as_layout",
      "catalog_template",
      "design_layout",
      //design
      "design_card",
      "design_pages",
      "use_element",
      "catalog_images",
      "edit_text",
      //publish
      "preview",
      "share",
      "download_pdf",
    ] as const,
  },
  Showroom: {
    Type: "showroom" as const,
    Name: "Showroom",
    Steps: [
      "discover_showroom", // Initial discovery of the Showroom feature
      "setup_showroom", // Basic Showroom setup
      "customize_showroom", // Customizing Showroom appearance and behavior
      "showroom_products", // Managing products in Showroom
      "showroom_pages", // Creating custom pages
      "showroom_sharing", // Sharing and privacy options
    ] as const,
  },

  OrderManagement: {
    Type: "order_management" as const,
    Name: "Order Management",
    Steps: [
      "discover_orders", // Initial discovery of the Orders feature
      "setup_orders", // First-time setup of order forms and settings
      //"customize_order_form", // Customizing order form fields and design
      "configure_payments", // Setting up Stripe Connect for payments
      "create_first_order", // Creating first order manually
      "edit_order", // Editing the  order
      "manage_order_status", // Updating order status (accept/reject/close)
      "process_payments", // Processing payments and charges
      "export_orders", // Exporting order data to CSV
      "setup_notifications", // Configuring email notifications
    ] as const,
  },
  InventoryManagement: {
    Type: "inventory_management" as const,
    Name: "Inventory Management",
    Steps: [
      "discover_inventory", // Initial discovery of inventory management
      "view_inventory_grid", // First view of the inventory DataGrid
      "update_single_quantity", // Editing single product/variant quantity
      "bulk_quantity_update", // Using bulk selection and editing
      "use_inventory_filters", // Using text search and stock filters
      "export_inventory", // Exporting inventory data to CSV
      "inventory_workflow", // Understanding the complete inventory workflow
    ] as const,
  },
  Discovery: {
    Type: "discovery" as const,
    Name: "Advanced Features",
    Steps: [
      //account
      "add_user",
      "account_settings",

      //data
      "image_upload",
      "manage_images",
      "use_parameters",
      "product-catalog-mapping",

      //future
      "upload_pdf",
      "view_analytics",
      "use_automation",

      //conversion
      "upgrade_plan",
    ] as const,
  },
} as const;

export type JourneyKeys = keyof typeof UserJourneys;
export type JourneyType = (typeof UserJourneys)[JourneyKeys]["Type"];

export type JourneyConfig<T extends JourneyType> = Extract<
  {
    [K in JourneyKeys]: {
      Type: (typeof UserJourneys)[K]["Type"];
    } & (typeof UserJourneys)[K];
  }[JourneyKeys],
  { Type: T }
>;

export type JourneySteps<T extends JourneyType> =
  JourneyConfig<T>["Steps"][number];

export type GuideDisplayMode = "expanded" | "collapsed" | "hidden";

// Define types for page configurations
type BasePageConfig = {
  paths: readonly string[];
  mode: GuideDisplayMode;
};

type JourneyPageConfig<T extends JourneyType = JourneyType> = BasePageConfig & {
  type: "journey";
  journeyType: T;
  journeyStep: JourneySteps<T>;
};

// Simple page configuration
type SimplePageConfig = BasePageConfig & {
  type: "simple";
};

// Union type for all page configurations
type PageConfig = JourneyPageConfig | SimplePageConfig;

// // Type guard to check if a config is a journey config
// function isJourneyConfig(config: PageConfig): config is JourneyPageConfig {
//   return config.type === "journey";
// }

// Helper function to create journey page configs with type checking
function createJourneyConfig<T extends JourneyType>(
  config: Omit<JourneyPageConfig<T>, "type"> & { type?: "journey" }
): JourneyPageConfig<T> {
  return {
    type: "journey",
    ...config,
  };
}

export const pageGuideConfigs: readonly PageConfig[] = [
  createJourneyConfig({
    paths: ["/products", "/products/add", "/import/shopify"] as const,
    journeyType: "onboarding",
    journeyStep: "first_product",
    mode: "collapsed" as const,
  }),
  createJourneyConfig({
    paths: ["/catalogs/create"] as const,
    journeyType: "onboarding",
    journeyStep: "first_catalog",
    mode: "collapsed" as const,
  }),
  createJourneyConfig({
    paths: ["/design/catalog"] as const,
    journeyType: "catalog_creation",
    journeyStep: "first_page",
    mode: "collapsed" as const,
  }),
  // Showroom related paths
  createJourneyConfig({
    paths: ["/showrooms"] as const,
    journeyType: "showroom",
    journeyStep: "discover_showroom",
    mode: "collapsed" as const,
  }),
  // createJourneyConfig({
  //   paths: ["/showrooms/new", "/showrooms/create"] as const,
  //   journeyType: "discovery",
  //   journeyStep: "setup_showroom",
  //   mode: "collapsed" as const,
  // }),
  createJourneyConfig({
    paths: ["/showrooms/:id"] as const,
    journeyType: "showroom",
    journeyStep: "customize_showroom",
    mode: "collapsed" as const,
  }),
  // Orders related paths
  createJourneyConfig({
    paths: ["/orders"] as const,
    journeyType: "order_management",
    journeyStep: "discover_orders",
    mode: "collapsed" as const,
  }),
  createJourneyConfig({
    paths: ["/orders/settings"] as const,
    journeyType: "order_management",
    journeyStep: "setup_orders",
    mode: "collapsed" as const,
  }),
  createJourneyConfig({
    paths: ["/orders/new", "/orders/:id/edit"] as const,
    journeyType: "order_management",
    journeyStep: "create_first_order",
    mode: "collapsed" as const,
  }),
  createJourneyConfig({
    paths: ["/orders/:id"] as const,
    journeyType: "order_management",
    journeyStep: "manage_order_status",
    mode: "collapsed" as const,
  }),
  // Inventory related paths
  createJourneyConfig({
    paths: ["/products/inventory"] as const,
    journeyType: "inventory_management",
    journeyStep: "discover_inventory",
    mode: "collapsed" as const,
  }),
  {
    type: "simple" as const,
    paths: ["/start"] as const,
    mode: "hidden" as const,
  },
] as const;

export interface JourneyProgress<T extends JourneyType = JourneyType> {
  journeyType: T;
  stepKey: JourneySteps<T>;
  completedAt?: string;
  source?: string;
  metadata?: Record<string, unknown>;
}
